import usePrivateLink from "../../config/usePrivateLink";
import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
} from "react-router-dom";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Footer from "../footer";
import * as XLSX from "xlsx";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";

import "react-calendar/dist/Calendar.css";
import {
  ExpenseChartData,
  GetExpenseReport,
} from "../../../api/ReportsServices";
import { getBatchList } from "../../../api/BatchService";
import {
  formatDate,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../../master/Master";
import { getSessions } from "../../../api/SessionsService";
import { BankList } from "../../../api/BankService";
import { setPrintContent } from "../../../PrintStore";
import { StaffList } from "../../../api/Staff";
import ReportLayout from "./ReportLayout";

export default function ExpenseReport() {
  const hasAccess = usePrivateLink();
  const [page, setPage] = useState(1);
  const [Data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [batches, setBatches] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [name, setName] = useState("");
  // const [from, setFrom] = useState("");
  // const [to, setTo] = useState("");
  const [batchId, setBatchID] = useState("");
  const [staffId, setStaffId] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSessions] = useState("");
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState("");
  const [transition, setTransition] = useState([]);
  const [type, setType] = useState("");
  const [banks, setBanks] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const navigate = useNavigate();
  const [overView, setOverView] = useState("1");
  const [chart, setChart] = useState([]);

  const getBusinessBanks = async () => {
    setLoading(true);
    try {
      const response = await BankList();
      if (response?.data?.status) {
        setBanks(response?.data?.data);
      } else {
        // ToastMain.fire({
        //   icon: 'info',
        //   title: response?.data?.message || 'something went wrong'
        // })
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      // ToastMain.fire({
      //   icon: 'error',
      //   title: error?.response?.data?.message || 'something went wrong'
      // })

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBusinessBanks();
  }, []);

  const getDefaultDates = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      if (!(date instanceof Date)) date = new Date(date); // ensure it's a Date
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDayOfMonth),
      to: formatDate(today),
    };
  };
  const { from: defaultFrom, to: defaultTo } = getDefaultDates();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const getSession = async () => {
    setLoading(true);
    try {
      const response = await getSessions();
      if (response?.data?.status) {
        const activeSession = response?.data?.data.find(
          (session) => session.status === "1",
        );
        setSelectedSessions(activeSession.id);
        setSessions(response?.data?.data);
        // setSessions(activeSessions);
      } else {
        setError("Failed to fetch session?.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  const handleExportAll = async () => {
    try {
      setLoading(true);

      // 🔹 Build dynamic query URL
      let addUrl = `&page=${page}&session_id=${selectedSession}`;
      if (from) addUrl += `&start_date=${from}`;
      if (to) addUrl += `&end_date=${to}`;
      if (batchId) addUrl += `&batch_id=${batchId}`;
      if (selectedAccount) addUrl += `&bank_id=${selectedAccount}`;

      // 🔹 Fetch first page
      const firstResponse = await GetExpenseReport(`${addUrl}&page=1`);
      let allData = Array.isArray(firstResponse?.data?.data?.data)
        ? firstResponse.data.data.data
        : [];

      const lastPage = firstResponse?.data?.data?.last_page || 1;

      if (!allData.length) {
        alert("No data to export!");
        setLoading(false);
        return;
      }

      // 🔹 Fetch remaining pages (if paginated)
      for (let page = 2; page <= lastPage; page++) {
        const response = await GetExpenseReport(`${addUrl}&page=${page}`);
        const records = Array.isArray(response?.data?.data?.data)
          ? response.data.data.data
          : [];
        allData.push(...records);
      }

      // 🔹 Format API data for Excel
      const formattedData = allData.map((item, index) => {
        const expense = item.expense || {};
        const bank = item.bank || {};
        const staff = item.staff || {};

        return {
          "Sr.No": index + 1,
          Date: item.date || "",
          "Expense Title": expense.title || "",
          Description: item.description || "",
          Type: item.type || "",
          "Payment Type": item.payment_type || "",
          "Amount (₹)": item.amount || "",
          "Bank Name": bank.bank_name || "Cash",
          "Staff Name": staff.name || "N/A",
          "Created At": new Date(item.created_at).toLocaleDateString(),
        };
      });

      // 🔹 Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const expenseSheet = XLSX.utils.json_to_sheet(formattedData);

      // 🔹 Add sheet to workbook
      XLSX.utils.book_append_sheet(workbook, expenseSheet, "Expense Report");

      // 🔹 Export to file
      XLSX.writeFile(workbook, "ExpenseReport.xlsx");

      ToastMain.fire({
        icon: "success",
        title: `Exported ${allData.length} expense records successfully!`,
      });
    } catch (error) {
      console.error(error);
      // ToastMain.fire({
      //   icon: "error",
      //   title: error.message || "Error exporting data!",
      // });

      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOnce = useRef(false);

  const fetchData = async () => {
    setLoading(true);
    let addUrl = `&page=${page}&session_id=${selectedSession}`;
    if (from) addUrl += `&start_date=${from}`;
    if (to) addUrl += `&end_date=${to}`;
    // if (batchId) addUrl += `&batch_id=${batchId}`;
    if (selectedAccount) addUrl += `&bank_id=${selectedAccount}`;
    try {
      const response = await GetExpenseReport(addUrl);
      setData(response?.data?.data);
      setTransition(response.data?.data || []);
      setTotal(response?.data?.total_sum);
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataChart = async () => {
    setLoading(true);
    let addUrl = `&page=${page}&session_id=${selectedSession}`;
    if (from) addUrl += `&start_date=${from}`;
    if (to) addUrl += `&end_date=${to}`;
    try {
      const response = await ExpenseChartData(addUrl);
      setChart(response?.data);
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const expenseCategory =
    chart?.data?.map((item) => ({
      category: item.title,
      amount: Number(item.total_amount),
    })) || [];

  const groupedExpenseCategory = Object.values(
    expenseCategory.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = {
          category: item.category,
          amount: 0,
        };
      }
      acc[item.category].amount += item.amount;
      return acc;
    }, {}),
  );

  useEffect(() => {
    fetchDataChart();
  }, [page, from, to, name, selectedSession, selectedAccount]);

  const getBatches = async () => {
    const addUrl = `&session_id=${selectedSession}`;
    setLoading(true);
    try {
      let response = await getBatchList(addUrl);

      setBatches(response?.data?.batches);
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStaffs = async () => {
    setLoading(true);
    try {
      let response = await StaffList();
      setStaffs(response?.data?.batches);
    } catch (error) {
      console.error("Failed to fetch chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaffs();
  }, []);

  useEffect(() => {
    getBatches();
  }, [selectedSession]);

  useEffect(() => {
    fetchData();
  }, [page, from, to, name, selectedSession, selectedAccount]);
  const handlePrint = async () => {
    // Open immediately because browsers can block a popup
    // if window.open() is called after an await.
    const printWindow = window.open("", "_blank", "width=1200,height=800");

    if (!printWindow) {
      SweetAlertShow(
        "warning",
        "Popup Blocked",
        "Please allow popups for this website and try again.",
      );
      return;
    }

    try {
      setLoading(true);

      // =========================================================
      // BUILD FILTER URL
      // =========================================================
      let addUrl = `&session_id=${selectedSession}`;

      if (from) {
        addUrl += `&start_date=${from}`;
      }

      if (to) {
        addUrl += `&end_date=${to}`;
      }

      if (selectedAccount) {
        addUrl += `&bank_id=${selectedAccount}`;
      }

      // =========================================================
      // FETCH FIRST PAGE
      // =========================================================
      const firstResponse = await GetExpenseReport(`${addUrl}&page=1`);

      const firstPage = firstResponse?.data?.data;

      let allData = Array.isArray(firstPage?.data) ? [...firstPage.data] : [];

      const lastPage = Number(firstPage?.last_page || 1);

      // =========================================================
      // FETCH ALL REMAINING PAGES
      // =========================================================
      for (let currentPage = 2; currentPage <= lastPage; currentPage++) {
        const response = await GetExpenseReport(
          `${addUrl}&page=${currentPage}`,
        );

        const records = response?.data?.data?.data;

        if (Array.isArray(records)) {
          allData.push(...records);
        }
      }

      // =========================================================
      // NO DATA
      // =========================================================
      if (!allData.length) {
        printWindow.close();

        SweetAlertShow(
          "info",
          "No Data",
          "There are no expense records available for printing.",
        );

        return;
      }

      // =========================================================
      // TOTAL
      // =========================================================
      const printTotal = allData.reduce(
        (sum, item) => sum + Number(item?.amount || 0),
        0,
      );

      // =========================================================
      // ACCOUNT NAME
      // =========================================================
      const selectedBank = banks?.find(
        (bank) => String(bank?.id) === String(selectedAccount),
      );

      // =========================================================
      // ESCAPE HTML
      // =========================================================
      const escapeHtml = (value) => {
        if (value === null || value === undefined) {
          return "";
        }

        return String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };

      // =========================================================
      // BUILD TABLE ROWS
      // =========================================================
      const rows = allData
        .map((item, index) => {
          const accountHtml = item?.bank
            ? `
              <strong>
                ${escapeHtml(item.bank.account_no)}
              </strong>
              ${
                item.bank.owner_name
                  ? `<br>
                     <span>
                       ${escapeHtml(item.bank.owner_name)}
                     </span>`
                  : ""
              }
            `
            : "Cash";

          return `
          <tr>
            <td class="sr">${index + 1}</td>

            <td>
              ${escapeHtml(formatDate(item?.date))}
            </td>

            <td>
              ${accountHtml}
            </td>

            <td>
              ${escapeHtml(item?.expense?.title || "N/A")}
            </td>

            <td>
              ${escapeHtml(item?.description || "-")}
            </td>

            <td>
              ${escapeHtml(item?.staff?.name || "-")}
            </td>

            <td class="amount">
              ₹${Number(item?.amount || 0).toLocaleString("en-IN")}
            </td>
          </tr>
        `;
        })
        .join("");

      // =========================================================
      // COMPLETE PRINT HTML
      // =========================================================
      const html = `
      <!DOCTYPE html>

      <html>
        <head>

          <meta charset="UTF-8" />

          <title>Expense Report</title>

          <style>

            * {
              box-sizing: border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #000;
              font-family:
                Arial,
                Helvetica,
                sans-serif;
            }

            body {
              font-size: 10px;
            }

            @page {
              size: A4 portrait;
              margin: 8mm;
            }

            .print-container {
              width: 100%;
              margin: 0;
              padding: 0;
            }

            /* =========================================
               HEADER
            ========================================= */

            .report-header {
              width: 100%;
              text-align: center;
              margin: 0 0 8px 0;
              padding: 0;
            }

            .report-header h1 {
              margin: 0 0 3px 0;
              padding: 0;
              font-size: 20px;
              line-height: 1.2;
              font-weight: 700;
            }

            .report-header .date-range {
              font-size: 11px;
              line-height: 1.4;
            }

            /* =========================================
               SUMMARY
            ========================================= */

            .summary {
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 10px;
              margin: 6px 0 10px 0;
              padding: 6px 8px;
              border: 1px solid #999;
              background: #f8f8f8;
              font-size: 10px;
            }

            .summary-item {
              white-space: nowrap;
            }

            /* =========================================
               TABLE
            ========================================= */

            table {
              width: 100%;
              border-collapse: collapse;
              border-spacing: 0;
              table-layout: fixed;
              margin: 0;
              padding: 0;
            }

            thead {
              display: table-header-group;
            }

            tbody {
              display: table-row-group;
            }

            tr {
              page-break-inside: avoid;
              break-inside: avoid;
            }

            th,
            td {
              border: 1px solid #777;
              padding: 4px 5px;
              font-size: 9px;
              line-height: 1.2;
              vertical-align: middle;
              word-wrap: break-word;
              overflow-wrap: anywhere;
            }

            th {
              background: #eeeeee !important;
              color: #000 !important;
              font-weight: 700;
              text-align: left;
            }

            /* =========================================
               COLUMN WIDTHS
            ========================================= */

            .sr {
              width: 5%;
              text-align: center;
            }

            .date {
              width: 10%;
            }

            .account {
              width: 20%;
            }

            .expense {
              width: 14%;
            }

            .description {
              width: 27%;
            }

            .staff {
              width: 10%;
            }

            .amount {
              width: 14%;
              text-align: right;
              white-space: nowrap;
            }

            /* =========================================
               TOTAL
            ========================================= */

            .total-row {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              font-weight: 700;
            }

            .total-row th {
              background: #eeeeee !important;
              font-weight: 700;
            }

            /* =========================================
               PRINT
            ========================================= */

            @media print {

              html,
              body {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                background: #fff !important;
              }

              .print-container {
                width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
              }

              .report-header {
                margin-top: 0 !important;
                padding-top: 0 !important;
              }

              table {
                margin-top: 0 !important;
              }

              thead {
                display: table-header-group !important;
              }

              tr {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }

              .total-row {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }

          </style>

        </head>

        <body>

          <div class="print-container">

            <!-- =========================================
                 REPORT HEADER
            ========================================== -->

            <div class="report-header">

              <h1>
                Expense Report
              </h1>

              <div class="date-range">

                <strong>
                  ${escapeHtml(from ? formatDate(from) : "All Dates")}
                </strong>

                &nbsp; to &nbsp;

                <strong>
                  ${escapeHtml(to ? formatDate(to) : "All Dates")}
                </strong>

              </div>

              ${
                selectedAccount && selectedBank
                  ? `
                    <div class="date-range">
                      Account:
                      <strong>
                        ${escapeHtml(selectedBank.account_no)}
                      </strong>
                      ${
                        selectedBank.owner_name
                          ? ` - ${escapeHtml(selectedBank.owner_name)}`
                          : ""
                      }
                    </div>
                  `
                  : ""
              }

            </div>


            <!-- =========================================
                 SUMMARY
            ========================================== -->

            <div class="summary">

              <div class="summary-item">
                <strong>
                  Total Transactions:
                </strong>

                ${allData.length}
              </div>

              <div class="summary-item">
                <strong>
                  Total Expense:
                </strong>

                ₹${printTotal.toLocaleString("en-IN")}
              </div>

              <div class="summary-item">
                <strong>
                  Printed On:
                </strong>

                ${new Date().toLocaleDateString("en-IN")}
              </div>

            </div>


            <!-- =========================================
                 EXPENSE TABLE
            ========================================== -->

            <table>

              <colgroup>
                <col class="sr">
                <col class="date">
                <col class="account">
                <col class="expense">
                <col class="description">
                <col class="staff">
                <col class="amount">
              </colgroup>

              <thead>

                <tr>

                  <th class="sr">
                    #
                  </th>

                  <th class="date">
                    Date
                  </th>

                  <th class="account">
                    Account
                  </th>

                  <th class="expense">
                    Expense
                  </th>

                  <th class="description">
                    Description
                  </th>

                  <th class="staff">
                    Staff
                  </th>

                  <th class="amount">
                    Amount
                  </th>

                </tr>

              </thead>


              <tbody>

                ${rows}

                <!-- FINAL TOTAL -->
                <tr class="total-row">

                  <th
                    colspan="6"
                    style="text-align:right;"
                  >
                    TOTAL
                  </th>

                  <th
                    class="amount"
                    style="text-align:right;"
                  >
                    ₹${printTotal.toLocaleString("en-IN")}
                  </th>

                </tr>

              </tbody>

            </table>

          </div>

        </body>
      </html>
    `;

      // =========================================================
      // WRITE TO NEW WINDOW
      // =========================================================
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      // =========================================================
      // PRINT AFTER DOCUMENT LOAD
      // =========================================================
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();

          // Don't close immediately because some browsers
          // need the print dialog to remain open.
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        }, 300);
      };
    } catch (error) {
      console.error("Failed to prepare print:", error);

      printWindow.close();

      SweetAlertShow(
        "error",
        "Print Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to prepare the expense report.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (index) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue}, 65%, 50%)`;
  };

  const colors = groupedExpenseCategory.map((_, index) =>
    getCategoryColor(index),
  );

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-3">
                {/* =====================================================
                  REPORT NAVIGATION
              ====================================================== */}
                <div className="mb-3">
                  <ReportLayout />
                </div>

                {/* =====================================================
                  PAGE HEADER
              ====================================================== */}
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                            style={{
                              width: "42px",
                              height: "42px",
                            }}
                          >
                            <i className="ri-wallet-3-line fs-4"></i>
                          </div>

                          <div>
                            <h4 className="mb-0 fw-bold">Expense Report</h4>

                            <small className="text-muted">
                              Track and analyse your expenses
                            </small>
                          </div>
                        </div>
                      </div>

                      <div
                        className="form-floating form-floating-outline"
                        style={{ minWidth: "220px" }}
                      >
                        <select
                          className="form-select"
                          value={selectedSession}
                          onChange={(e) => setSelectedSessions(e.target.value)}
                        >
                          {sessions?.map((session) => (
                            <option key={session?.id} value={session?.id}>
                              {session?.session}
                              {session?.status === "1"
                                ? " • Active"
                                : " • Inactive"}
                            </option>
                          ))}
                        </select>

                        <label>Academic Session</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =====================================================
                  FILTERS
              ====================================================== */}
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <h6 className="mb-0 fw-bold">
                          <i className="ri-filter-3-line me-2 text-primary"></i>
                          Report Filters
                        </h6>

                        <small className="text-muted">
                          Select a date range and account
                        </small>
                      </div>

                      <span className="badge bg-label-primary rounded-pill px-3 py-2">
                        {from && to
                          ? `${formatDate(from)} - ${formatDate(to)}`
                          : "All Dates"}
                      </span>
                    </div>

                    <div className="row g-3">
                      {/* From */}
                      <div className="col-12 col-md-4">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                          />

                          <label>From Date</label>
                        </div>
                      </div>

                      {/* To */}
                      <div className="col-12 col-md-4">
                        <div className="form-floating form-floating-outline">
                          <input
                            type="date"
                            className="form-control"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                          />

                          <label>To Date</label>
                        </div>
                      </div>

                      {/* Bank */}
                      <div className="col-12 col-md-4">
                        <div className="form-floating form-floating-outline">
                          <select
                            className="form-select"
                            value={selectedAccount}
                            onChange={(e) => setSelectedAccount(e.target.value)}
                          >
                            <option value="">All Accounts</option>

                            {banks?.map((item) => (
                              <option key={item?.id} value={item?.id}>
                                {item?.account_no} - {item?.owner_name}
                              </option>
                            ))}
                          </select>

                          <label>Payment Account</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =====================================================
                  KPI CARDS
              ====================================================== */}
                <div className="row g-3 mb-3">
                  {/* Total */}
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted d-block mb-1">
                              Total Expense
                            </small>

                            <h4 className="mb-1 fw-bold">
                              ₹{Number(total || 0).toLocaleString("en-IN")}
                            </h4>

                            <small className="text-muted">
                              Selected period
                            </small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-danger"
                            style={{
                              width: "45px",
                              height: "45px",
                            }}
                          >
                            <i className="ri-money-rupee-circle-line fs-4 text-danger"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transactions */}
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted d-block mb-1">
                              Transactions
                            </small>

                            <h4 className="mb-1 fw-bold">{Data?.total || 0}</h4>

                            <small className="text-muted">
                              Expense entries
                            </small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                            style={{
                              width: "45px",
                              height: "45px",
                            }}
                          >
                            <i className="ri-file-list-3-line fs-4 text-primary"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Average */}
                  <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <small className="text-muted d-block mb-1">
                              Average Expense
                            </small>

                            <h4 className="mb-1 fw-bold">
                              ₹
                              {Data?.total
                                ? Number(total / Data.total).toLocaleString(
                                    "en-IN",
                                    {
                                      maximumFractionDigits: 0,
                                    },
                                  )
                                : "0"}
                            </h4>

                            <small className="text-muted">
                              Per transaction
                            </small>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-warning"
                            style={{
                              width: "45px",
                              height: "45px",
                            }}
                          >
                            <i className="ri-bar-chart-2-line fs-4 text-warning"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =====================================================
                  MAIN REPORT CARD
              ====================================================== */}
                <div className="card border-0 shadow-sm">
                  {/* Toolbar */}
                  <div className="card-header border-bottom p-3">
                    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
                      {/* View switch */}
                      <div>
                        <div className="btn-group">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              overView === "1"
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() => setOverView("1")}
                          >
                            <i className="ri-list-check-2 me-1"></i>
                            Transactions
                          </button>

                          {hasAccess("/reports/expense/report/overview") && (
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                overView === "2"
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              onClick={() => setOverView("2")}
                            >
                              <i className="ri-pie-chart-2-line me-1"></i>
                              Overview
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="d-flex gap-2">
                        {hasAccess("/reports/expense/report/print") && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line me-1"></i>
                            Print
                          </button>
                        )}

                        {hasAccess("/reports/expense/report/export") && (
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={handleExportAll}
                          >
                            <i className="ri-file-excel-2-line me-1"></i>
                            Export Excel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* =====================================================
                    TRANSACTION VIEW
                ====================================================== */}
                  {overView === "1" && (
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead>
                            <tr>
                              <th className="ps-4" style={{ width: "70px" }}>
                                #
                              </th>

                              <th>Date</th>
                              <th>Account</th>
                              <th>Description</th>

                              <th className="text-end pe-4">Amount</th>
                            </tr>
                          </thead>

                          <tbody>
                            {Data?.data?.length > 0 ? (
                              Data.data.map((item, index) => (
                                <tr key={item?.id || index}>
                                  <td className="ps-4">
                                    <span className="text-muted fw-medium">
                                      {(Data?.from || 1) + index}
                                    </span>
                                  </td>

                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <div
                                        className="rounded-2 bg-label-primary d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "36px",
                                          height: "36px",
                                        }}
                                      >
                                        <i className="ri-calendar-line text-primary"></i>
                                      </div>

                                      <div>
                                        <div className="fw-medium">
                                          {formatDate(item?.date)}
                                        </div>

                                        {item?.created_at && (
                                          <small className="text-muted">
                                            {new Date(
                                              item.created_at,
                                            ).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    {item?.bank ? (
                                      <div>
                                        <div className="fw-medium">
                                          {item.bank.account_no}
                                        </div>

                                        <small className="text-muted">
                                          {item.bank.owner_name}
                                        </small>
                                      </div>
                                    ) : (
                                      <span className="badge bg-label-secondary">
                                        Cash
                                      </span>
                                    )}
                                  </td>

                                  <td>
                                    <div className="fw-medium">
                                      {item?.expense?.title || "Expense"}
                                    </div>

                                    {item?.description && (
                                      <small
                                        className="text-muted d-block text-truncate"
                                        style={{ maxWidth: "350px" }}
                                      >
                                        {item.description}
                                      </small>
                                    )}

                                    {item?.staff?.name && (
                                      <span className="badge bg-label-info mt-1">
                                        <i className="ri-user-line me-1"></i>
                                        {item.staff.name}
                                      </span>
                                    )}
                                  </td>

                                  <td className="text-end pe-4">
                                    <span className="fw-bold text-danger">
                                      - ₹
                                      {Number(item?.amount || 0).toLocaleString(
                                        "en-IN",
                                      )}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="5" className="text-center py-5">
                                  <div className="mb-2">
                                    <i className="ri-inbox-line fs-1 text-muted"></i>
                                  </div>

                                  <h6 className="mb-1">No expenses found</h6>

                                  <small className="text-muted">
                                    Try changing the date range or account
                                    filter.
                                  </small>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {Data?.total > 0 && (
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 p-3 border-top">
                          <small className="text-muted">
                            Showing <strong>{Data?.from}</strong> to{" "}
                            <strong>{Data?.to}</strong> of{" "}
                            <strong>{Data?.total}</strong> entries
                          </small>

                          <nav>
                            <ul className="pagination pagination-sm mb-0">
                              {Data?.links?.map((btn, i) => {
                                const isFirst = i === 0;
                                const isLast = i === Data.links.length - 1;

                                return (
                                  <li
                                    key={i}
                                    className={`page-item ${
                                      btn?.active ? "active" : ""
                                    } ${!btn?.url ? "disabled" : ""}`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      onClick={() => {
                                        if (!btn?.url) return;

                                        if (isLast) {
                                          setPage(Data.current_page + 1);
                                        } else if (isFirst) {
                                          setPage(Data.current_page - 1);
                                        } else {
                                          setPage(Number(btn.label));
                                        }
                                      }}
                                    >
                                      {isFirst ? (
                                        <i className="ri-arrow-left-s-line"></i>
                                      ) : isLast ? (
                                        <i className="ri-arrow-right-s-line"></i>
                                      ) : (
                                        btn.label
                                      )}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </nav>
                        </div>
                      )}
                    </div>
                  )}

                  {/* =====================================================
                    OVERVIEW
                ====================================================== */}
                  {overView === "2" && (
                    <div className="card-body">
                      {/* =====================================================
        OVERVIEW HEADER
    ====================================================== */}
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                        <div>
                          <h5 className="mb-1 fw-bold">Expense Overview</h5>
                          <small className="text-muted">
                            Category-wise expense analysis
                          </small>
                        </div>

                        <div className="mt-3 mt-md-0">
                          <span className="badge bg-label-danger fs-6 px-3 py-2">
                            Total ₹
                            {groupedExpenseCategory
                              .reduce(
                                (sum, item) => sum + Number(item.amount || 0),
                                0,
                              )
                              .toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>

                      {/* =====================================================
        PIE CHART
    ====================================================== */}
                      <div className="card border shadow-none mb-3">
                        <div className="card-header border-bottom">
                          <h6 className="mb-1 fw-bold">Expense Breakdown</h6>
                          <small className="text-muted">
                            Distribution by category
                          </small>
                        </div>

                        <div className="card-body">
                          {groupedExpenseCategory.length > 0 ? (
                            <div
                              style={{
                                width: "100%",
                                height: "380px",
                              }}
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart
                                  margin={{
                                    top: 20,
                                    right: 30,
                                    bottom: 20,
                                    left: 30,
                                  }}
                                >
                                  <Pie
                                    data={groupedExpenseCategory}
                                    dataKey="amount"
                                    nameKey="category"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={75}
                                    outerRadius={135}
                                    paddingAngle={2}
                                    strokeWidth={2}
                                  >
                                    {groupedExpenseCategory.map(
                                      (entry, index) => (
                                        <Cell
                                          key={`pie-${index}`}
                                          fill={colors[index]}
                                        />
                                      ),
                                    )}
                                  </Pie>

                                  <Tooltip
                                    formatter={(value) =>
                                      `₹${Number(value).toLocaleString("en-IN")}`
                                    }
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="text-center py-5 text-muted">
                              <i className="ri-pie-chart-2-line fs-1 d-block mb-2"></i>
                              No expense data available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =====================================================
        CATEGORY LIST
    ====================================================== */}
                      <div className="card border shadow-none mb-3">
                        <div className="card-header border-bottom">
                          <h6 className="mb-1 fw-bold">Expense Categories</h6>
                          <small className="text-muted">
                            Category-wise amount
                          </small>
                        </div>

                        <div className="card-body">
                          {groupedExpenseCategory.length > 0 ? (
                            <div
                              className="expense-category-grid"
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fit, minmax(280px, 1fr))",
                                gap: "0 24px",
                              }}
                            >
                              {groupedExpenseCategory.map((entry, index) => (
                                <div
                                  key={index}
                                  className="d-flex align-items-center justify-content-between py-3 border-bottom"
                                >
                                  <div className="d-flex align-items-center gap-3 min-w-0">
                                    <span
                                      className="rounded-circle flex-shrink-0"
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        backgroundColor: colors[index],
                                      }}
                                    />

                                    <div className="min-w-0">
                                      <div
                                        className="fw-medium text-truncate"
                                        title={entry.category}
                                      >
                                        {entry.category}
                                      </div>

                                      <small className="text-muted">
                                        Expense category
                                      </small>
                                    </div>
                                  </div>

                                  <strong className="ms-3 text-nowrap">
                                    ₹
                                    {Number(entry.amount).toLocaleString(
                                      "en-IN",
                                    )}
                                  </strong>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-5 text-muted">
                              No categories available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =====================================================
        BAR CHART
    ====================================================== */}
                      <div className="card border shadow-none mb-3">
                        <div className="card-header border-bottom">
                          <h6 className="mb-1 fw-bold">Expense by Category</h6>
                          <small className="text-muted">
                            Comparative category analysis
                          </small>
                        </div>

                        <div className="card-body">
                          {groupedExpenseCategory.length > 0 ? (
                            <div
                              style={{
                                width: "100%",
                                height: Math.max(
                                  400,
                                  groupedExpenseCategory.length * 45,
                                ),
                              }}
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={groupedExpenseCategory}
                                  layout="vertical"
                                  margin={{
                                    top: 10,
                                    right: 30,
                                    left: 150,
                                    bottom: 10,
                                  }}
                                >
                                  <XAxis
                                    type="number"
                                    tickFormatter={(value) =>
                                      `₹${Number(value).toLocaleString("en-IN")}`
                                    }
                                  />

                                  <YAxis
                                    type="category"
                                    dataKey="category"
                                    width={140}
                                    tick={{ fontSize: 12 }}
                                  />

                                  <Tooltip
                                    formatter={(value) =>
                                      `₹${Number(value).toLocaleString("en-IN")}`
                                    }
                                  />

                                  <Bar dataKey="amount" radius={[0, 6, 6, 0]}>
                                    {groupedExpenseCategory.map(
                                      (entry, index) => (
                                        <Cell
                                          key={`bar-${index}`}
                                          fill={colors[index]}
                                        />
                                      ),
                                    )}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="text-center py-5 text-muted">
                              No chart data available
                            </div>
                          )}
                        </div>
                      </div>

                      {/* =====================================================
        TOTAL SUMMARY
    ====================================================== */}
                      <div className="card border shadow-none">
                        <div className="card-body">
                          <div className="row align-items-center">
                            <div className="col-auto">
                              <div
                                className="rounded-circle bg-label-danger d-flex align-items-center justify-content-center"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                }}
                              >
                                <i className="ri-money-rupee-circle-line fs-3 text-danger"></i>
                              </div>
                            </div>

                            <div className="col">
                              <small className="text-muted d-block">
                                Total Expense
                              </small>

                              <h3 className="fw-bold mb-0">
                                ₹
                                {groupedExpenseCategory
                                  .reduce(
                                    (sum, x) => sum + Number(x.amount || 0),
                                    0,
                                  )
                                  .toLocaleString("en-IN")}
                              </h3>
                            </div>

                            <div className="col-auto">
                              <span className="badge bg-label-primary rounded-pill px-3 py-2">
                                {groupedExpenseCategory.length} Categories
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Footer />

              <div className="content-backdrop fade"></div>
            </div>
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>
    </>
  );
}
